import React from 'react';
import { Alert, Box, Button, Link, Paper, Stack, Typography } from '@mui/material';
import type { SafetyScanWorkflowViewModel } from '../../../domain/acceptance/safetyScanWorkflowViewModel';
import { useSafetyScanRecordPreview } from './useSafetyScanRecordPreview';
import { ScanCameraMeasurementBasis } from '../../../components/cameraMeasurement/ScanCameraMeasurementBasis';
import { ScanControlRelationDisclosure } from '../../../components/safetyCases/ScanControlRelationDisclosure';
import type { ScanMeasurementSource } from '../../../domain/cameraMeasurement/scanMeasurementIdentity';
import type { ScanControlRelationView } from '../../../domain/safetyCases/scanControlTemporalRelation';
import type { CameraActivityMeasurementResponse } from '../../../services/cameraActivityMeasurementApi';

function Section({
  id,
  number,
  title,
  children,
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      data-testid={`safety-scan-workflow-${id}`}
      sx={{ p: { xs: 2, md: 2.25 }, borderRadius: 2.5 }}
    >
      <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.12 }}>
        {`${number}. ${title}`}
      </Typography>
      <Box sx={{ mt: 1 }}>{children}</Box>
    </Paper>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function SafetyScanWorkflowStrip({
  model,
}: {
  model: SafetyScanWorkflowViewModel['strip'];
}) {
  return (
    <Box
      data-testid="safety-scan-workflow-strip"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
        gap: 1,
      }}
    >
      {model.map((item) => (
        <Box
          key={item.id}
          data-testid={`safety-scan-workflow-strip-${item.id}`}
          data-current={item.current ? 'true' : 'false'}
          data-complete={item.complete ? 'true' : 'false'}
          sx={{
            px: 1,
            py: 1.1,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: item.current ? 'primary.main' : 'divider',
            bgcolor: item.current ? 'action.selected' : 'background.paper',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.16, display: 'block' }}>
            {`${item.number} ${item.label}`}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function SafetyScanWorkflowPage({
  model,
  onReviewEpisodes,
  onSign,
  onOpenSignedRecord,
  onDownloadSignedRecord,
  signDisabled,
  advanced,
  measurementSource,
  measurementOverride = null,
  controlRelation = null,
}: {
  model: SafetyScanWorkflowViewModel;
  onReviewEpisodes?: () => void;
  onSign?: () => void;
  onOpenSignedRecord?: () => void;
  onDownloadSignedRecord?: () => void;
  signDisabled?: boolean;
  advanced?: React.ReactNode;
  measurementSource?: ScanMeasurementSource | null;
  measurementOverride?: CameraActivityMeasurementResponse | null;
  controlRelation?: ScanControlRelationView | null;
}) {
  const { preview } = useSafetyScanRecordPreview(model.sign.previewReportId);
  const signed = model.signedRecord.signed;

  return (
    <Stack spacing={1.5} data-testid="safety-scan-workflow-page">
      <SafetyScanWorkflowStrip model={model.strip} />

      <Section id="current-scan" number={1} title="CURRENT SCAN">
        <Typography variant="h5" sx={{ fontWeight: 800 }} data-testid="safety-scan-workflow-condition">
          {model.currentScan.conditionLabel}
        </Typography>
        <Typography variant="body1" sx={{ mt: 0.5 }} data-testid="safety-scan-workflow-location">
          {model.currentScan.locationLine}
        </Typography>
        <Box sx={{ mt: 1.5, display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
          <Fact label="Scan window" value={model.currentScan.windowLabel} />
          <Fact label="Safety Case" value={model.currentScan.caseLabel} />
          <Fact label="State" value={model.currentScan.stateLabel} />
          <Fact label="Control" value={model.currentScan.controlRecordedLabel} />
          <Fact label="Site" value={model.currentScan.siteLabel} />
          <Fact label="Camera" value={model.currentScan.cameraLabel} />
        </Box>
        <Box sx={{ mt: 1.25 }}>
          <ScanControlRelationDisclosure relation={controlRelation} />
        </Box>
      </Section>

      {measurementSource ? (
        <ScanCameraMeasurementBasis
          source={{ ...measurementSource, signed: model.signedRecord.signed || measurementSource.signed }}
          override={measurementOverride}
        />
      ) : null}

      <Paper variant="outlined" data-testid="safety-scan-comparison-reference" sx={{ p: { xs: 2, md: 2.25 }, borderRadius: 2.5 }}>
        <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.12 }}>
          CASE CONTROL
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }} data-testid="safety-scan-evaluation-boundary">
          Compared on the Safety Case, not in this Scan.
        </Typography>
        <Link href={model.history.href} data-testid="safety-scan-case-evaluation-link" sx={{ display: 'inline-block', mt: 1 }}>
          Open Safety Case Controls / Evaluation
        </Link>
      </Paper>

      <Section id="review" number={2} title="REVIEW REQUIRED EPISODES">
        <Typography variant="h6" sx={{ fontWeight: 800 }} data-testid="safety-scan-workflow-review-headline">
          {model.review.headline}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.75 }} data-testid="safety-scan-workflow-review-detail">
          {model.review.detail}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {[model.review.reviewedLabel, model.review.candidateLabel].filter(Boolean).join(' · ')}
        </Typography>
        {model.review.required && onReviewEpisodes ? (
          <Button
            variant="contained"
            sx={{ mt: 1.5 }}
            onClick={onReviewEpisodes}
            data-testid="safety-scan-review-required-episodes"
          >
            {model.review.ctaLabel}
          </Button>
        ) : null}
      </Section>

      {signed ? (
        <Section id="record" number={3} title="SIGNED RECORD">
          <Typography variant="h6" sx={{ fontWeight: 800 }} data-testid="safety-scan-workflow-record-headline">
            {model.signedRecord.headline}
          </Typography>
          {model.signedRecord.signedAtLabel ? (
            <Typography variant="body2" sx={{ mt: 0.75 }} data-testid="safety-scan-signed-at">
              {`Signed ${model.signedRecord.signedAtLabel}`}
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            {model.signedRecord.detail}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
            {onOpenSignedRecord || model.signedRecord.href ? (
              <Button
                variant="contained"
                onClick={onOpenSignedRecord}
                href={onOpenSignedRecord ? undefined : (model.signedRecord.href || undefined)}
                data-testid="safety-scan-open-signed-record"
              >
                {model.signedRecord.viewCtaLabel}
              </Button>
            ) : null}
            {onDownloadSignedRecord ? (
              <Button
                variant="outlined"
                onClick={onDownloadSignedRecord}
                data-testid="safety-scan-download-signed-record"
              >
                {model.signedRecord.downloadCtaLabel}
              </Button>
            ) : null}
          </Stack>
        </Section>
      ) : (
        <Section id="sign" number={3} title="READY TO SIGN">
          <Typography variant="h6" sx={{ fontWeight: 800 }} data-testid="safety-scan-workflow-sign-headline">
            {model.sign.headline}
          </Typography>
          <Alert severity="info" sx={{ mt: 1.25 }} data-testid="safety-scan-sign-copy">
            {model.sign.helper}
          </Alert>
          {model.sign.disabledReason ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="safety-scan-workflow-sign-blocked">
              {model.sign.disabledReason}
            </Typography>
          ) : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
            {model.sign.previewAvailable ? (
              <Button
                variant="outlined"
                onClick={() => { void preview(); }}
                data-testid="safety-scan-preview-record"
              >
                {model.sign.previewCtaLabel}
              </Button>
            ) : null}
            {onSign ? (
              <Button
                variant="contained"
                disabled={signDisabled || !model.sign.ready}
                onClick={onSign}
                data-testid="safety-scan-sign-button"
              >
                {model.sign.ctaLabel}
              </Button>
            ) : null}
          </Stack>
        </Section>
      )}

      <Typography variant="body2" data-testid="safety-scan-history-link">
        <Link href={model.history.href}>{model.history.label}</Link>
      </Typography>

      {advanced ? (
        <details data-testid="safety-scan-workflow-advanced">
          <summary>
            <Typography component="span" variant="subtitle2" sx={{ fontWeight: 800 }}>
              Advanced
            </Typography>
          </summary>
          <Box sx={{ mt: 1.25 }}>
            <Alert severity="info" sx={{ mb: 1.5 }} data-testid="safety-scan-download-helper">
              {model.downloads.helper}
            </Alert>
            {advanced}
          </Box>
        </details>
      ) : null}
    </Stack>
  );
}
